<template>
  <div>
    <!-- ─── Top stat cards ─────────────────────────────────────────────────── -->
    <b-row class="mb-2">
      <b-col
        cols="12"
        md="6"
        lg="3"
        class="mb-1"
      >
        <b-card
          no-body
          class="bg-gradient-success text-white"
        >
          <b-card-body class="py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <small class="text-white-50">Toplam Realize PnL</small>
                <h3 class="text-white mb-0">
                  {{ totalPnlPrefix }}{{ formatNum(summary && summary.totalRealizedPnl, 4) }}
                </h3>
                <small class="text-white-50">{{ summary ? summary.closedTrades : 0 }} kapanmış trade</small>
              </div>
              <feather-icon
                icon="DollarSignIcon"
                size="36"
                class="opacity-50"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        md="6"
        lg="3"
        class="mb-1"
      >
        <b-card
          no-body
          :class="todayCardClass"
        >
          <b-card-body class="py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <small class="text-white-50">Bugün PnL</small>
                <h3 class="text-white mb-0">
                  {{ todayPnlPrefix }}{{ formatNum(summary && summary.todayPnl, 4) }}
                </h3>
                <small class="text-white-50">7g: {{ formatNum(summary && summary.weekPnl, 2) }} · 30g: {{ formatNum(summary && summary.monthPnl, 2) }}</small>
              </div>
              <feather-icon
                icon="TrendingUpIcon"
                size="36"
                class="opacity-50"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        md="6"
        lg="3"
        class="mb-1"
      >
        <b-card
          no-body
          class="bg-gradient-info text-white"
        >
          <b-card-body class="py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <small class="text-white-50">Aktif Bot</small>
                <h3 class="text-white mb-0">
                  {{ runningBotCount }}<small class="text-white-50">/{{ totalBotCount }}</small>
                </h3>
                <small class="text-white-50">{{ activeTargetCount }} aktif hedef</small>
              </div>
              <feather-icon
                icon="CpuIcon"
                size="36"
                class="opacity-50"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        md="6"
        lg="3"
        class="mb-1"
      >
        <b-card
          no-body
          :class="positionCardClass"
        >
          <b-card-body class="py-2">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <small class="text-white-50">Açık Pozisyon</small>
                <h3 class="text-white mb-0">
                  {{ summary ? summary.openPositions : 0 }}
                </h3>
                <small class="text-white-50">
                  Unrealized: {{ unrealizedPrefix }}{{ formatNum(summary && summary.totalUnrealizedPnl, 4) }}
                </small>
              </div>
              <feather-icon
                icon="ActivityIcon"
                size="36"
                class="opacity-50"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- ─── Middle row: Equity Curve + Notifications ──────────────────────── -->
    <b-row class="mb-2">
      <b-col
        cols="12"
        lg="8"
        class="mb-1"
      >
        <b-card class="h-100">
          <div class="d-flex align-items-center mb-1">
            <h5 class="mb-0">
              <feather-icon icon="TrendingUpIcon" /> Equity Curve (son 14 gün)
            </h5>
            <b-button
              variant="flat-primary"
              size="sm"
              class="ml-auto"
              :to="{ name: 'Reports' }"
            >
              Detaylı Rapor <feather-icon icon="ArrowRightIcon" />
            </b-button>
          </div>
          <div
            v-if="!pnlTimeline.length"
            class="text-muted text-center py-3"
          >
            Henüz trade verisi yok. Bir bot oluşturup başlat.
          </div>
          <vue-apex-charts
            v-else
            type="area"
            height="240"
            :options="equityChartOptions"
            :series="equityChartSeries"
          />
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="4"
        class="mb-1"
      >
        <b-card class="h-100">
          <div class="d-flex align-items-center mb-1">
            <h5 class="mb-0">
              <feather-icon icon="BellIcon" /> Son Bildirimler
            </h5>
            <b-badge
              v-if="unreadCount > 0"
              variant="danger"
              class="ml-1"
            >
              {{ unreadCount }}
            </b-badge>
            <b-button
              v-if="latestNotifications.length"
              variant="flat-secondary"
              size="sm"
              class="ml-auto"
              @click="markAllRead"
            >
              Tümünü Okundu İşaretle
            </b-button>
          </div>
          <div
            v-if="!latestNotifications.length"
            class="text-muted text-center py-3"
          >
            Henüz bildirim yok.
          </div>
          <div
            v-else
            class="notifications-list"
          >
            <div
              v-for="n in latestNotifications"
              :key="n.id"
              class="d-flex py-1 border-bottom"
            >
              <feather-icon
                :icon="iconFor(n.type)"
                :class="`text-${n.type} mt-50 mr-1`"
                size="18"
              />
              <div
                class="flex-grow-1"
                style="min-width:0;"
              >
                <div
                  class="font-small-3"
                  :class="!n.read ? 'font-weight-bold' : ''"
                >
                  {{ n.message }}
                </div>
                <small class="text-muted">
                  {{ formatDateTime(n.timestamp) }}
                  <span v-if="n.source"> · {{ n.source.kind }} #{{ n.source.id }}</span>
                </small>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- ─── Bottom row: Running bots + Recent trades ──────────────────────── -->
    <b-row>
      <b-col
        cols="12"
        lg="7"
        class="mb-1"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-header class="d-flex align-items-center py-1">
            <h5 class="mb-0">
              <feather-icon icon="ZapIcon" /> Çalışan Botlar
            </h5>
            <b-button
              variant="flat-primary"
              size="sm"
              class="ml-auto"
              :to="{ name: 'Bots' }"
            >
              Tümünü Gör <feather-icon icon="ArrowRightIcon" />
            </b-button>
          </b-card-header>
          <b-table
            :items="runningBots"
            :fields="botFields"
            small
            striped
            responsive
            show-empty
            empty-text="Çalışan bot yok."
          >
            <template #cell(name)="data">
              <strong>{{ data.item.name }}</strong>
              <div class="font-small-2 text-muted">
                <b-badge
                  :variant="data.item.mode === 'LIVE' ? 'danger' : 'info'"
                  class="mr-1"
                >
                  {{ data.item.mode }}
                </b-badge>
                {{ data.item.symbol || '—' }} · {{ data.item.timeframe || '—' }}
              </div>
            </template>
            <template #cell(realizedPnl)="data">
              <span
                :class="(Number(data.item.realizedPnl) || 0) >= 0 ? 'text-success' : 'text-danger'"
                class="font-weight-bold"
              >
                {{ (Number(data.item.realizedPnl) || 0) >= 0 ? '+' : '' }}{{ formatNum(data.item.realizedPnl, 4) }}
              </span>
            </template>
            <template #cell(status)="data">
              <b-badge variant="success">
                {{ data.item.status }}
              </b-badge>
            </template>
          </b-table>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="5"
        class="mb-1"
      >
        <b-card
          no-body
          class="h-100"
        >
          <b-card-header class="d-flex align-items-center py-1">
            <h5 class="mb-0">
              <feather-icon icon="ListIcon" /> Son Finans Hareketleri
            </h5>
            <b-button
              variant="flat-primary"
              size="sm"
              class="ml-auto"
              :to="{ name: 'Reports' }"
            >
              Tümü <feather-icon icon="ArrowRightIcon" />
            </b-button>
          </b-card-header>
          <b-table
            :items="recentTrades"
            :fields="tradeFields"
            small
            striped
            responsive
            show-empty
            empty-text="Henüz trade yok."
          >
            <template #cell(occurredAt)="data">
              <small>{{ formatDateTimeShort(data.item.occurredAt) }}</small>
            </template>
            <template #cell(symbol)="data">
              <div class="font-small-3">
                <strong>{{ financeSymbol(data.item) }}</strong>
              </div>
              <div class="font-small-2 text-muted">
                {{ financeSubtitle(data.item) }}
              </div>
            </template>
            <template #cell(action)="data">
              <b-badge
                :variant="financeActionVariant(data.item)"
                class="mr-1"
              >
                {{ financeActionLabel(data.item) }}
              </b-badge>
              <b-badge
                v-if="data.item.side && data.item.side !== '-'"
                :variant="data.item.side === 'LONG' ? 'success' : 'danger'"
              >
                {{ data.item.side }}
              </b-badge>
            </template>
            <template #cell(pnl)="data">
              <span
                v-if="financePnlValue(data.item) !== null"
                :class="financePnlValue(data.item) >= 0 ? 'text-success' : 'text-danger'"
              >
                {{ financePnlValue(data.item) >= 0 ? '+' : '' }}{{ formatNum(financePnlValue(data.item), 2) }}
              </span>
              <span
                v-else
                class="text-muted"
              >—</span>
            </template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardBody, BCardHeader, BTable, BBadge, BButton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'Manager',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardHeader,
    BTable,
    BBadge,
    BButton,
    VueApexCharts,
  },
  data() {
    return {
      refreshTimer: null,
      refreshInFlight: false,
      refreshIntervalMs: 10000,
      botFields: [
        { key: 'name', label: 'Bot' },
        { key: 'realizedPnl', label: 'Realize PnL', thClass: 'width-130' },
        { key: 'status', label: 'Durum', thClass: 'width-90 text-center' },
      ],
      tradeFields: [
        { key: 'occurredAt', label: 'Zaman', thClass: 'width-120' },
        { key: 'symbol', label: 'Sembol/Bot' },
        { key: 'action', label: 'Aksiyon' },
        { key: 'pnl', label: 'PnL' },
      ],
    }
  },
  computed: {
    summary() { return this.$store.getters['reports/summary'] },
    pnlTimeline() { return this.$store.getters['reports/pnlTimeline'] },
    recentTrades() { return (this.$store.getters['finance/recentTrades'] || []).slice(0, 8) },
    runningBots() {
      const list = this.$store.getters['bots/dataList'] || []
      return list.filter(b => b.status === 'RUNNING' || b.status === 'PAUSED').slice(0, 8)
    },
    latestNotifications() { return this.$store.getters['notifications/latest'] || [] },
    unreadCount() { return this.$store.getters['notifications/unreadCount'] || 0 },
    runningBotCount() {
      if (!this.summary) return 0
      return Number(this.summary.botCounts && this.summary.botCounts.RUNNING) || 0
    },
    totalBotCount() {
      if (!this.summary || !this.summary.botCounts) return 0
      return Object.values(this.summary.botCounts).reduce((a, b) => a + Number(b), 0)
    },
    activeTargetCount() {
      if (!this.summary) return 0
      return Number(this.summary.targetCounts && this.summary.targetCounts.ACTIVE) || 0
    },
    totalPnlPrefix() {
      return this.summary && this.summary.totalRealizedPnl >= 0 ? '+' : ''
    },
    todayPnlPrefix() {
      return this.summary && this.summary.todayPnl >= 0 ? '+' : ''
    },
    unrealizedPrefix() {
      return this.summary && this.summary.totalUnrealizedPnl >= 0 ? '+' : ''
    },
    todayCardClass() {
      if (!this.summary) return 'bg-gradient-warning text-white'
      return this.summary.todayPnl >= 0 ? 'bg-gradient-success text-white' : 'bg-gradient-danger text-white'
    },
    positionCardClass() {
      if (!this.summary) return 'bg-gradient-primary text-white'
      return this.summary.totalUnrealizedPnl >= 0 ? 'bg-gradient-primary text-white' : 'bg-gradient-danger text-white'
    },
    equityCurve() {
      let cum = 0
      return this.pnlTimeline.map(p => {
        cum += Number(p.pnl)
        return { date: p.date, cum: Number(cum.toFixed(4)) }
      })
    },
    equityChartOptions() {
      const series = this.equityCurve
      const lastVal = series.length ? series[series.length - 1].cum : 0
      const positive = lastVal >= 0
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        stroke: { curve: 'smooth', width: 2 },
        colors: [positive ? '#28c76f' : '#ea5455'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100],
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: series.map(p => p.date.slice(5)),
          labels: { rotate: -45, style: { fontSize: '10px' } },
        },
        yaxis: { labels: { formatter: v => Number(v).toFixed(2) } },
        grid: { borderColor: '#e0e0e0' },
        markers: { size: 0 },
        tooltip: {
          custom: ({ dataPointIndex }) => {
            const p = series[dataPointIndex]
            if (!p) return ''
            return `<div class="px-1 py-1"><div><strong>${p.date}</strong></div><div>Kümülatif: <strong>${p.cum.toFixed(4)}</strong></div></div>`
          },
        },
      }
    },
    equityChartSeries() {
      return [{ name: 'Kümülatif PnL', data: this.equityCurve.map(p => p.cum) }]
    },
  },
  created() {
    this.fetchAll()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  methods: {
    fetchAll() {
      if (this.refreshInFlight) return Promise.resolve()
      this.refreshInFlight = true

      return Promise.allSettled([
        this.$store.dispatch('reports/getSummary'),
        this.$store.dispatch('reports/getPnlTimeline', { days: 14 }),
        this.$store.dispatch('finance/getRecentTrades', { limit: 8 }),
        this.$store.dispatch('bots/getDataList', { status: 'RUNNING' }),
      ]).finally(() => {
        this.refreshInFlight = false
      })
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.refreshTimer = setInterval(() => {
        this.fetchAll()
      }, this.refreshIntervalMs)
    },
    stopAutoRefresh() {
      if (!this.refreshTimer) return
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    },
    markAllRead() {
      this.$store.dispatch('notifications/markAllRead')
    },
    iconFor(type) {
      const map = {
        danger: 'AlertOctagonIcon', warning: 'AlertCircleIcon', success: 'AwardIcon', info: 'InfoIcon',
      }
      return map[type] || 'BellIcon'
    },
    formatNum(v, d = 2) {
      if (v === null || v === undefined) return '—'
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(d) : '—'
    },
    formatDateTime(v) {
      if (!v) return '—'
      try {
        const d = new Date(v)
        if (isNaN(d.getTime())) return String(v)
        return d.toLocaleString('tr-TR', {
          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        })
      } catch (e) { return String(v) }
    },
    formatDateTimeShort(v) {
      if (!v) return '—'
      try {
        const d = new Date(v)
        const now = new Date()
        const sameDay = d.toDateString() === now.toDateString()
        if (sameDay) return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
        return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })
      } catch (e) { return String(v) }
    },
    financeSymbol(item) {
      return item.symbol || item.asset || item.type || '—'
    },
    financeSubtitle(item) {
      if (item.type === 'INITIAL_BALANCE') return item.reason || 'Başlangıç bakiyesi'
      if (item.type === 'WITHDRAWAL') return item.reason || 'Çekim'
      if (item.type === 'DEPOSIT') return item.reason || 'Bakiye girişi'
      if (item.type === 'ADJUSTMENT') return item.reason || 'Düzeltme'
      return item.botName || '—'
    },
    financeActionLabel(item) {
      if (item.type === 'INITIAL_BALANCE') return 'BAŞLANGIÇ'
      if (item.type === 'WITHDRAWAL') return 'ÇEKİM'
      if (item.type === 'DEPOSIT') return 'GİRİŞ'
      if (item.type === 'ADJUSTMENT') return 'DÜZELTME'
      return item.action || item.type || '—'
    },
    financeActionVariant(item) {
      if (item.type === 'INITIAL_BALANCE') return 'primary'
      if (item.type === 'WITHDRAWAL') return 'danger'
      if (item.type === 'DEPOSIT') return 'success'
      if (item.type === 'ADJUSTMENT') return 'secondary'
      return item.action === 'ENTRY' ? 'info' : 'warning'
    },
    financePnlValue(item) {
      if (item.pnl !== null && item.pnl !== undefined) return Number(item.pnl)
      if (item.type === 'DEPOSIT' || item.type === 'WITHDRAWAL') return Number(item.amount || 0)
      return null
    },
  },
}
</script>

<style scoped>
.notifications-list {
  max-height: 280px;
  overflow-y: auto;
}
.notifications-list .border-bottom:last-child {
  border-bottom: none !important;
}
.bg-gradient-success {
  background: linear-gradient(118deg, #28c76f, #28c76f99);
}
.bg-gradient-info {
  background: linear-gradient(118deg, #00cfe8, #00cfe899);
}
.bg-gradient-danger {
  background: linear-gradient(118deg, #ea5455, #ea545599);
}
.bg-gradient-warning {
  background: linear-gradient(118deg, #ff9f43, #ff9f4399);
}
.bg-gradient-primary {
  background: linear-gradient(118deg, #7367f0, #7367f099);
}
.opacity-50 {
  opacity: 0.5;
}
</style>
